import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import api from '../../api/api';
import { RebateReportResult, RebateReportResultItem } from '../../model/RebateReportResult';

const Style = styled.div`
    margin-top: 1rem;
    max-width: 600px;

    h3 {
        margin-bottom: 0.5rem;
    }
    .error {
        color: red;
    }

    .rebate-table {
        margin-bottom: 1.5rem;
    }
    .rebate-table .number {
        text-align: right;
    }
`;

interface IProps {
    userId?: string;
}

const QuarterReport: React.FC<{ report: RebateReportResult, prior?: boolean }> = ({ report, prior }) => {
    if (!report.items.length) return <p><em>No paid invoices for {report.quarterStart} - {report.quarterEnd}.</em></p>;

    const totalRebate = report.items.reduce((sum, item) => sum + item.rebateAmount, 0);
    const label = prior ? 'Prior Rebate Report' : 'Rebate Report';
    const headingStyle = prior ? { fontWeight: 'normal' as const, fontStyle: 'italic' as const, fontSize: '0.9em' } : {};

    return (
        <>
            <h3 style={headingStyle}>{label} ({report.quarterStart} - {report.quarterEnd}) — Total: ${totalRebate.toFixed(2)}</h3>
            <table className="table rebate-table">
                <thead>
                    <tr>
                        <th>Ticker</th>
                        <th>Invoice #</th>
                        <th>Paid On</th>
                        <th className="number">Invoice Total</th>
                        <th className="number">Rebate Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {report.items.map((item: RebateReportResultItem, i: number) =>
                        item.invoices.map((inv, j) => (
                            <tr key={`${i}-${j}`}>
                                <td>{item.combinedTicker}</td>
                                <td>{inv.invoiceNumber}</td>
                                <td>{moment(inv.fullyPaidOnDate).format('D-MMM-YY')}</td>
                                <td className="number">${inv.subTotal.toFixed(2)}</td>
                                <td className="number">${item.rebateAmount.toFixed(2)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </>
    );
};

const UserRebateReport: React.FC<IProps> = ({ userId }) => {
    const [currentQuarter, setCurrentQuarter] = useState<RebateReportResult | undefined>(undefined);
    const [previousQuarter, setPreviousQuarter] = useState<RebateReportResult | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    useAsyncEffect(async (isMounted) => {
        try {
            const [current, previous] = userId
                ? await Promise.all([api().getRebateReport(userId, 0), api().getRebateReport(userId, -1)])
                : await Promise.all([api().getMyRebateReport(0), api().getMyRebateReport(-1)]);
            if (isMounted()) {
                setCurrentQuarter(current);
                setPreviousQuarter(previous);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }, [userId]);

    if (loading) return <Style>Loading rebate report...</Style>;

    const hasError = currentQuarter?.errorMessage || previousQuarter?.errorMessage;
    if (hasError) return <Style><div className="error">{currentQuarter?.errorMessage || previousQuarter?.errorMessage}</div></Style>;

    const hasData = (currentQuarter?.items?.length ?? 0) > 0 || (previousQuarter?.items?.length ?? 0) > 0;
    if (!hasData && !currentQuarter && !previousQuarter) return null;

    return (
        <Style>
            {currentQuarter && <QuarterReport report={currentQuarter} />}
            {previousQuarter && <QuarterReport report={previousQuarter} prior />}
        </Style>
    );
};

export default UserRebateReport;
